import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Plus, Book, Calendar, ChevronLeft, ChevronRight, 
  Edit3, Trash2, Save, X, Clock, Sparkles, Crown, Leaf, 
  BarChart3, Users, Settings, Check, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { hapticSuccess } from '../lib/nativeFeel';
import { isNativePlatform } from '../lib/native';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Mood tags
const MOOD_TAGS = [
  { id: 'grateful', label: 'Grateful', color: '#8BAA9E' },
  { id: 'motivated', label: 'Motivated', color: '#C6A75E' },
  { id: 'peaceful', label: 'Peaceful', color: '#7BA3C9' },
  { id: 'reflective', label: 'Reflective', color: '#9B8AA6' },
  { id: 'hopeful', label: 'Hopeful', color: '#A8C686' },
  { id: 'challenged', label: 'Challenged', color: '#C9A07B' },
];

// Format date for diary display
const formatDiaryDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
};

// Format date for list preview
const formatPreviewDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  });
};

const MyJournal = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [sortOrder, setSortOrder] = useState('desc');
  
  // View state
  const [view, setView] = useState('list'); // list | view | edit | new
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [currentEntryIndex, setCurrentEntryIndex] = useState(0);
  
  // Editor state
  const [editorTitle, setEditorTitle] = useState('');
  const [editorContent, setEditorContent] = useState('');
  const [editorMood, setEditorMood] = useState(null);
  const [saving, setSaving] = useState(false);
  const [autoSaveTimer, setAutoSaveTimer] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Calendar state
  const [showCalendar, setShowCalendar] = useState(false);
  const [datesWithEntries, setDatesWithEntries] = useState([]);
  
  const editorRef = useRef(null);
  const isPremium = user?.is_premium;

  // Fetch diary entries
  const fetchEntries = useCallback(async (pageNum = 1, append = false) => {
    try {
      const res = await fetch(
        `${API_URL}/api/diary/entries?page=${pageNum}&limit=20&sort=${sortOrder}`,
        { credentials: 'include' }
      );
      
      if (res.ok) {
        const data = await res.json();
        if (append) {
          setEntries(prev => [...prev, ...data.entries]);
        } else {
          setEntries(data.entries);
        }
        setTotalEntries(data.total);
        setHasMore(data.has_more);
        setPage(pageNum);
      } else if (res.status === 403) {
        // Premium required - handled by UI
      }
    } catch (error) {
      console.error('[MyJournal] Failed to fetch entries:', error);
    } finally {
      setLoading(false);
    }
  }, [API_URL, sortOrder]);

  // Fetch dates with entries (for calendar)
  const fetchDatesWithEntries = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/diary/dates`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setDatesWithEntries(data.dates || []);
      }
    } catch (error) {
      console.error('[MyJournal] Failed to fetch entry dates:', error);
    }
  }, [API_URL]);

  useEffect(() => {
    if (isPremium) {
      fetchEntries();
      fetchDatesWithEntries();
    } else {
      setLoading(false);
    }
  }, [isPremium, fetchEntries, fetchDatesWithEntries]);

  // Auto-save functionality
  useEffect(() => {
    if (view === 'edit' && hasUnsavedChanges && selectedEntry) {
      // Clear existing timer
      if (autoSaveTimer) clearTimeout(autoSaveTimer);
      
      // Set new auto-save timer (10 seconds)
      const timer = setTimeout(() => {
        handleSaveEntry(true);
      }, 10000);
      
      setAutoSaveTimer(timer);
      
      return () => clearTimeout(timer);
    }
  }, [editorContent, editorTitle, editorMood, hasUnsavedChanges, view, selectedEntry]);

  // Create new entry
  const handleCreateEntry = async () => {
    if (!editorContent.trim()) {
      toast.error('Please write something in your journal');
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/diary/entries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: editorTitle.trim() || null,
          content: editorContent.trim(),
          mood_tag: editorMood
        })
      });

      if (res.ok) {
        const newEntry = await res.json();
        setEntries(prev => [newEntry, ...prev]);
        setTotalEntries(prev => prev + 1);
        toast.success('Journal entry saved');
        hapticSuccess();
        resetEditor();
        setView('list');
        fetchDatesWithEntries();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save entry');
      }
    } catch (error) {
      toast.error('Failed to save entry');
    } finally {
      setSaving(false);
    }
  };

  // Save/update entry
  const handleSaveEntry = async (isAutoSave = false) => {
    if (!selectedEntry || !editorContent.trim()) return;

    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/diary/entries/${selectedEntry.entry_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: editorTitle.trim() || null,
          content: editorContent.trim(),
          mood_tag: editorMood
        })
      });

      if (res.ok) {
        const updated = await res.json();
        setEntries(prev => prev.map(e => 
          e.entry_id === updated.entry_id ? updated : e
        ));
        setSelectedEntry(updated);
        setHasUnsavedChanges(false);
        
        if (!isAutoSave) {
          toast.success('Changes saved');
          hapticSuccess();
          setView('view');
        } else {
          toast('Auto-saved', { duration: 1500 });
        }
      }
    } catch (error) {
      if (!isAutoSave) toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Delete entry — HARD DELETE (permanent, GDPR-compliant)
  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('This entry will be permanently deleted and cannot be recovered.')) return;

    try {
      const res = await fetch(`${API_URL}/api/diary/entries/${entryId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (res.ok) {
        setEntries(prev => prev.filter(e => e.entry_id !== entryId));
        setTotalEntries(prev => prev - 1);
        toast.success('Journal entry permanently deleted.');
        setView('list');
        setSelectedEntry(null);
      } else {
        toast.error('We could not delete this entry. Please try again.');
      }
    } catch (error) {
      toast.error('We could not delete this entry. Please try again.');
    }
  };

  // Navigate between entries
  const goToPreviousEntry = () => {
    if (currentEntryIndex > 0) {
      const newIndex = currentEntryIndex - 1;
      setCurrentEntryIndex(newIndex);
      setSelectedEntry(entries[newIndex]);
    }
  };

  const goToNextEntry = () => {
    if (currentEntryIndex < entries.length - 1) {
      const newIndex = currentEntryIndex + 1;
      setCurrentEntryIndex(newIndex);
      setSelectedEntry(entries[newIndex]);
    }
  };

  // Jump to date
  const handleJumpToDate = async (date) => {
    try {
      const res = await fetch(`${API_URL}/api/diary/entries/by-date/${date}`, {
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data.entries.length > 0) {
          setSelectedEntry(data.entries[0]);
          setView('view');
        } else {
          toast('No entries on this date');
        }
      }
    } catch (error) {
      toast.error('Failed to load entries');
    }
    setShowCalendar(false);
  };

  // Open entry for viewing
  const openEntry = (entry, index) => {
    setSelectedEntry(entry);
    setCurrentEntryIndex(index);
    setView('view');
  };

  // Start editing
  const startEditing = () => {
    if (selectedEntry) {
      setEditorTitle(selectedEntry.title || '');
      setEditorContent(selectedEntry.content || '');
      setEditorMood(selectedEntry.mood_tag || null);
      setHasUnsavedChanges(false);
      setView('edit');
    }
  };

  // Start new entry
  const startNewEntry = () => {
    resetEditor();
    setView('new');
    setTimeout(() => {
      editorRef.current?.focus();
    }, 100);
  };

  // Reset editor
  const resetEditor = () => {
    setEditorTitle('');
    setEditorContent('');
    setEditorMood(null);
    setHasUnsavedChanges(false);
    setSelectedEntry(null);
  };

  // Handle content change with unsaved tracking
  const handleContentChange = (value) => {
    setEditorContent(value);
    if (view === 'edit') setHasUnsavedChanges(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1C1C1E] flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-[#8BAA9E] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Premium Gate
  if (!isPremium) {
    return (
      <div className="min-h-screen bg-[#1C1C1E] flex flex-col" data-testid="journal-premium-gate">
        <header className="px-6 pt-8 pb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="w-10 h-10 rounded-full bg-[#252528] flex items-center justify-center text-[#B5B1AB] hover:text-[#DCD3CA]"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <div>
              <p className="text-[#C6A75E] text-xs tracking-[0.2em] uppercase font-sans mb-1">Premium</p>
              <h1 className="font-serif text-2xl text-[#DCD3CA]">My Journal</h1>
            </div>
          </div>
        </header>

        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
          <div className="w-20 h-20 rounded-full bg-[#252528] flex items-center justify-center mb-6">
            <Book className="w-10 h-10 text-[#C6A75E]" strokeWidth={1.5} />
          </div>
          <h2 className="font-serif text-3xl text-[#DCD3CA] mb-4">
            Your Private Journal
          </h2>
          <p className="text-[#C5BCB2] font-sans mb-4 max-w-sm leading-relaxed">
            Capture your thoughts, reflections, and breakthroughs in a 
            beautiful chronological diary. Look back on your journey anytime.
          </p>
          <p className="text-[#7A7670] font-sans text-sm mb-8 max-w-xs italic">
            No metrics. No streaks. Just you and your thoughts.
          </p>
          <Button
            onClick={() => navigate(isNativePlatform() ? '/settings' : '/premium')}
            className="bg-gradient-to-r from-[#C6A75E] to-[#D4B56A] text-[#1C1C1E] font-medium px-8 py-6 rounded-full hover:opacity-90"
            data-testid="upgrade-btn"
          >
            <Crown className="w-5 h-5 mr-2" />
            Unlock My Journal
          </Button>
        </div>
      </div>
    );
  }

  // List View
  if (view === 'list') {
    return (
      <div className="min-h-screen bg-[#1C1C1E] pb-24" data-testid="journal-list-view">
        {/* Header */}
        <header className="px-6 pt-8 pb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="w-10 h-10 rounded-full bg-[#252528] flex items-center justify-center text-[#B5B1AB] hover:text-[#DCD3CA]"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
              </button>
              <div>
                <p className="text-[#8BAA9E] text-xs tracking-[0.2em] uppercase font-sans mb-1">Premium</p>
                <h1 className="font-serif text-2xl text-[#DCD3CA]">My Journal</h1>
              </div>
            </div>
            <button
              onClick={() => setShowCalendar(true)}
              className="w-10 h-10 rounded-full bg-[#252528] flex items-center justify-center text-[#B5B1AB] hover:text-[#DCD3CA]"
              data-testid="calendar-btn"
            >
              <Calendar className="w-5 h-5" strokeWidth={1.5} />
            </button>
          </div>
        </header>

        <div className="px-6 space-y-4">
          {/* New Entry Button */}
          <Button
            onClick={startNewEntry}
            className="w-full bg-[#8BAA9E] hover:bg-[#A3C2B6] text-[#1C1C1E] rounded-2xl py-6 flex items-center justify-center gap-2"
            data-testid="new-entry-btn"
          >
            <Plus className="w-5 h-5" />
            <span className="font-medium">New Entry</span>
          </Button>

          {/* Sort Toggle */}
          <div className="flex items-center justify-between">
            <span className="text-[#7A7670] text-sm font-sans">
              {totalEntries} {totalEntries === 1 ? 'entry' : 'entries'}
            </span>
            <button
              onClick={() => {
                setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
                setPage(1);
                fetchEntries(1);
              }}
              className="text-[#8BAA9E] text-sm font-sans flex items-center gap-1"
              data-testid="sort-toggle"
            >
              <Clock className="w-4 h-4" />
              {sortOrder === 'desc' ? 'Newest first' : 'Oldest first'}
            </button>
          </div>

          {/* Entries List */}
          {entries.length === 0 ? (
            <div className="text-center py-12">
              <Book className="w-12 h-12 text-[#7A7670] mx-auto mb-4" strokeWidth={1} />
              <p className="text-[#B5B1AB] font-sans">Your journal is empty</p>
              <p className="text-[#7A7670] text-sm font-sans mt-2">Start writing your first entry</p>
            </div>
          ) : (
            <div className="space-y-3">
              {entries.map((entry, index) => (
                <button
                  key={entry.entry_id}
                  onClick={() => openEntry(entry, index)}
                  className="w-full text-left bg-[#252528] border border-white/5 rounded-2xl p-5 hover:border-[#8BAA9E]/30 transition-all"
                  data-testid={`entry-${entry.entry_id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[#8BAA9E] text-xs font-sans">
                          {formatPreviewDate(entry.created_at)}
                        </span>
                        {entry.mood_tag && (
                          <span 
                            className="text-xs px-2 py-0.5 rounded-full"
                            style={{ 
                              backgroundColor: `${MOOD_TAGS.find(m => m.id === entry.mood_tag)?.color}20`,
                              color: MOOD_TAGS.find(m => m.id === entry.mood_tag)?.color 
                            }}
                          >
                            {MOOD_TAGS.find(m => m.id === entry.mood_tag)?.label}
                          </span>
                        )}
                      </div>
                      {entry.title && (
                        <p className="font-serif text-[#DCD3CA] mb-1 truncate">{entry.title}</p>
                      )}
                      <p className="text-[#B5B1AB] text-sm font-sans line-clamp-2 leading-relaxed">
                        {entry.content}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-[#7A7670] flex-shrink-0" />
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Load More */}
          {hasMore && (
            <Button
              onClick={() => fetchEntries(page + 1, true)}
              variant="outline"
              className="w-full border-[#666666] text-[#B5B1AB] rounded-xl py-3"
            >
              Load More
            </Button>
          )}
        </div>

        {/* Calendar Modal */}
        {showCalendar && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
            onClick={() => setShowCalendar(false)}
          >
            <div 
              className="bg-[#252528] border border-white/10 rounded-[24px] p-6 w-full max-w-sm"
              onClick={e => e.stopPropagation()}
            >
              <h3 className="font-serif text-xl text-[#DCD3CA] mb-4">Jump to Date</h3>
              <p className="text-[#7A7670] text-sm font-sans mb-4">
                Dates with entries are highlighted
              </p>
              <input
                type="date"
                onChange={(e) => handleJumpToDate(e.target.value)}
                className="w-full bg-[#1C1C1E] border border-white/10 rounded-xl p-3 text-[#DCD3CA]"
                data-testid="date-picker"
              />
              <Button
                onClick={() => setShowCalendar(false)}
                variant="outline"
                className="w-full mt-4 border-[#666666] text-[#B5B1AB] rounded-xl"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Bottom Navigation */}
        <nav
        className="fixed left-4 right-4 bg-[#1C1C1E]/90 backdrop-blur-xl border border-white/10 rounded-full h-16 flex items-center justify-around px-6 shadow-2xl z-40"
        style={{ bottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))' }}
      >
          <button 
            onClick={() => navigate('/dashboard')}
            className="flex flex-col items-center gap-1 text-[#7A7670] hover:text-[#B5B1AB]"
            data-testid="nav-home"
          >
            <Leaf className="w-5 h-5" strokeWidth={1.5} />
            <span className="text-[10px] font-sans">Home</span>
          </button>
          <button 
            onClick={() => navigate('/progress')}
            className="flex flex-col items-center gap-1 text-[#7A7670] hover:text-[#B5B1AB]"
            data-testid="nav-progress"
          >
            <BarChart3 className="w-5 h-5" strokeWidth={1.5} />
            <span className="text-[10px] font-sans">Progress</span>
          </button>
          <button 
            className="flex flex-col items-center gap-1 text-[#8BAA9E]"
            data-testid="nav-journal"
          >
            <Book className="w-5 h-5" strokeWidth={1.5} />
            <span className="text-[10px] font-sans">Journal</span>
          </button>
          <button 
            onClick={() => navigate('/settings')}
            className="flex flex-col items-center gap-1 text-[#7A7670] hover:text-[#B5B1AB]"
            data-testid="nav-settings"
          >
            <Settings className="w-5 h-5" strokeWidth={1.5} />
            <span className="text-[10px] font-sans">Settings</span>
          </button>
        </nav>
      </div>
    );
  }

  // Full Entry View (Reading Mode)
  if (view === 'view' && selectedEntry) {
    return (
      <div className="min-h-screen bg-[#1C1C1E]" data-testid="journal-view-mode">
        {/* Header */}
        <header className="px-6 pt-8 pb-4 flex items-center justify-between">
          <button
            onClick={() => setView('list')}
            className="w-10 h-10 rounded-full bg-[#252528] flex items-center justify-center text-[#B5B1AB] hover:text-[#DCD3CA]"
            data-testid="back-to-list-btn"
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={startEditing}
              className="w-10 h-10 rounded-full bg-[#252528] flex items-center justify-center text-[#B5B1AB] hover:text-[#DCD3CA]"
              data-testid="edit-btn"
              aria-label="Edit journal entry"
            >
              <Edit3 className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <button
              onClick={() => handleDeleteEntry(selectedEntry.entry_id)}
              className="w-10 h-10 rounded-full bg-[#252528] flex items-center justify-center text-[#B5B1AB] hover:text-[#CF6679]"
              data-testid="delete-btn"
            >
              <Trash2 className="w-5 h-5" strokeWidth={1.5} />
            </button>
          </div>
        </header>

        {/* Entry Navigation */}
        <div className="flex items-center justify-between px-6 py-2">
          <button
            onClick={goToPreviousEntry}
            disabled={currentEntryIndex === 0}
            className={`p-2 rounded-full ${currentEntryIndex === 0 ? 'text-[#7A7670] opacity-50' : 'text-[#B5B1AB] hover:text-[#DCD3CA]'}`}
            aria-label="Previous journal entry"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="text-[#7A7670] text-xs font-sans">
            {currentEntryIndex + 1} of {entries.length}
          </span>
          <button
            onClick={goToNextEntry}
            disabled={currentEntryIndex >= entries.length - 1}
            className={`p-2 rounded-full ${currentEntryIndex >= entries.length - 1 ? 'text-[#7A7670] opacity-50' : 'text-[#B5B1AB] hover:text-[#DCD3CA]'}`}
            aria-label="Next journal entry"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Full Entry Content - Diary Style */}
        <div className="px-8 sm:px-12 py-6 max-w-2xl mx-auto">
          {/* Date Header */}
          <p className="text-[#8BAA9E] text-sm tracking-wider uppercase font-sans mb-4">
            {formatDiaryDate(selectedEntry.created_at)}
          </p>

          {/* Mood Tag */}
          {selectedEntry.mood_tag && (
            <span 
              className="inline-block text-sm px-3 py-1 rounded-full mb-6"
              style={{ 
                backgroundColor: `${MOOD_TAGS.find(m => m.id === selectedEntry.mood_tag)?.color}20`,
                color: MOOD_TAGS.find(m => m.id === selectedEntry.mood_tag)?.color 
              }}
            >
              {MOOD_TAGS.find(m => m.id === selectedEntry.mood_tag)?.label}
            </span>
          )}

          {/* Title */}
          {selectedEntry.title && (
            <h1 className="font-serif text-3xl text-[#DCD3CA] mb-6 leading-tight">
              {selectedEntry.title}
            </h1>
          )}

          {/* Content */}
          <div className="prose prose-invert max-w-none">
            <p className="font-serif text-lg text-[#C5BCB2] leading-loose whitespace-pre-wrap">
              {selectedEntry.content}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // New Entry / Edit Mode (Writing Canvas)
  if (view === 'new' || view === 'edit') {
    const isNewEntry = view === 'new';
    
    return (
      <div className="min-h-screen bg-[#1C1C1E] flex flex-col" data-testid={isNewEntry ? "journal-new-mode" : "journal-edit-mode"}>
        {/* Header */}
        <header className="px-6 pt-8 pb-4 flex items-center justify-between flex-shrink-0">
          <button
            onClick={() => {
              if (hasUnsavedChanges) {
                if (window.confirm('You have unsaved changes. Discard them?')) {
                  resetEditor();
                  setView('list');
                }
              } else {
                resetEditor();
                setView(isNewEntry ? 'list' : 'view');
              }
            }}
            className="w-10 h-10 rounded-full bg-[#252528] flex items-center justify-center text-[#B5B1AB] hover:text-[#DCD3CA]"
            data-testid="cancel-btn"
          >
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
          
          <div className="flex items-center gap-2">
            {view === 'edit' && hasUnsavedChanges && (
              <span className="text-[#C6A75E] text-xs font-sans">Unsaved</span>
            )}
            <Button
              onClick={isNewEntry ? handleCreateEntry : () => handleSaveEntry(false)}
              disabled={saving || !editorContent.trim()}
              className="bg-[#8BAA9E] hover:bg-[#A3C2B6] text-[#1C1C1E] rounded-full px-5 py-2"
              data-testid="save-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {isNewEntry ? 'Save' : 'Update'}
                </>
              )}
            </Button>
          </div>
        </header>

        {/* Date Display */}
        <div className="px-8 sm:px-12 pt-4">
          <p className="text-[#8BAA9E] text-sm tracking-wider uppercase font-sans">
            {isNewEntry 
              ? formatDiaryDate(new Date().toISOString())
              : formatDiaryDate(selectedEntry?.created_at || new Date().toISOString())
            }
          </p>
        </div>

        {/* Mood Selector */}
        <div className="px-8 sm:px-12 py-4 flex flex-wrap gap-2">
          {MOOD_TAGS.map(mood => (
            <button
              key={mood.id}
              onClick={() => setEditorMood(editorMood === mood.id ? null : mood.id)}
              className={`text-xs px-3 py-1.5 rounded-full transition-all ${
                editorMood === mood.id
                  ? 'ring-2 ring-offset-2 ring-offset-[#1C1C1E]'
                  : 'opacity-60 hover:opacity-100'
              }`}
              style={{ 
                backgroundColor: `${mood.color}20`,
                color: mood.color,
                ringColor: editorMood === mood.id ? mood.color : 'transparent'
              }}
            >
              {mood.label}
            </button>
          ))}
        </div>

        {/* Writing Canvas */}
        <div className="flex-1 px-8 sm:px-12 overflow-y-auto pb-8">
          {/* Title Input */}
          <Input
            value={editorTitle}
            onChange={(e) => {
              setEditorTitle(e.target.value);
              if (view === 'edit') setHasUnsavedChanges(true);
            }}
            placeholder="Entry title (optional)"
            className="w-full bg-transparent border-none text-2xl sm:text-[26px] font-serif text-[#E2DAD1] placeholder:text-[#7A7670] p-0 mb-6 focus:ring-0 tracking-[0.005em]"
            data-testid="title-input"
          />

          {/* Content Textarea - Diary Style */}
          <Textarea
            ref={editorRef}
            value={editorContent}
            onChange={(e) => handleContentChange(e.target.value)}
            placeholder="Write your thoughts..."
            className="journal-editor w-full min-h-[420px] bg-transparent border-none text-xl sm:text-[22px] font-serif text-[#E2DAD1] placeholder:text-[#7A7670] p-0 resize-none focus:ring-0 tracking-[0.005em]"
            style={{ lineHeight: 1.75, fontWeight: 400 }}
            data-testid="content-input"
          />
        </div>
      </div>
    );
  }

  return null;
};

export default MyJournal;
